import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  Fab,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  TextField,
  Grid,
  FormControl,
  Checkbox,
  FormControlLabel,
  Divider
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { 
  LocationOn, 
  Home, 
  Person, 
  Phone, 
  Email,
  ArrowBack,
  Edit,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  AccountTree as AccountTreeIcon,
  Work as WorkIcon,
  People as PeopleIcon,
  CheckCircle as SoldIcon,
  Delete as DeleteIcon,
  AssignmentInd as ApplicationIcon
} from '@mui/icons-material';
import type { Property } from '../types/property';
import { getProperty, updateProperty, markPropertyAsSold } from '../services/propertyService';
import { isRentalApplicationSupported } from '../services/rentalApplicationService';
import { getWorkflows, detectAndCreateMissingWorkflow, shouldCreateNextWorkflow, createNextWorkflow, createSpecificWorkflow } from '../services/workflowService';
import PropertyWorkflow from '../components/PropertyWorkflow';
import PropertyDetailsContent from '../components/PropertyDetailsContent';
import OpenScheduleCard from '../components/OpenScheduleCard';
import PropertyContractorJobs from '../components/PropertyContractorJobs';
import WorkflowFiles from '../components/WorkflowFiles';
import PropertyAttendees from '../components/PropertyAttendees';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import socketService from '../services/socketService';
import { NumericFormat } from 'react-number-format';
import { getPropertyViewMode, type PropertyViewMode } from '../utils/roleUtils';

interface Workflow {
  _id: string;
  name: string;
  description?: string;
  type: 'sale' | 'rental' | 'auction' | 'consultation';
  status: 'pending' | 'in_progress' | 'completed';
  isActive: boolean;
  stages: any[]; // Define a proper type for stages if available
  currentStage: number;
  previousWorkflowId?: string; // Link to the previous workflow
  nextWorkflowId?: string; // Link to the next workflow
  propertyId?: string;
  agencyId?: string;
  createdAt?: string;
  updatedAt?: string;
}

const PropertyDetails: React.FC = () => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, agency } = useAuth();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactiveSubscriptionNotice, setShowInactiveSubscriptionNotice] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [expandedWorkflowId, setExpandedWorkflowId] = useState<string | null>(null);
  const [workflowsLoading, setWorkflowsLoading] = useState(true);
  const [workflowsError, setWorkflowsError] = useState<string | null>(null);
  const [refreshFilesSignal, setRefreshFilesSignal] = useState(0);
  const [missingWorkflow, setMissingWorkflow] = useState<{ workflow: Workflow; saleType: string } | null>(null);
  const [creatingWorkflow, setCreatingWorkflow] = useState(false);
  
  // Helper function to extract primary owner email from property.owner (can be single object or array)
  const getPrimaryOwnerEmail = (owner: Property['owner']): string | undefined => {
    if (!owner) return undefined;
    if (Array.isArray(owner)) {
      return owner[0]?.email;
    }
    return owner?.email;
  };
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [markSoldDialogOpen, setMarkSoldDialogOpen] = useState(false);
  const [markSoldFormData, setMarkSoldFormData] = useState({
    soldPrice: '',
    purchasers: [{
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      postcode: '',
      notes: '',
    }],
    createCustomer: true,
  });
  const [markingAsSold, setMarkingAsSold] = useState(false);
  // Use subscription object if available, fallback to legacy field
  const currentStatus = agency?.subscription?.status || agency?.subscriptionStatus;
  const isSubscriptionActive = currentStatus === 'active' || currentStatus === 'trial';
  const isAdmin = user?.primaryRole === 'admin';
  // Contractor jobs summary state
  const [contractorJobs, setContractorJobs] = useState<Array<{ _id: string; contractorName: string; contractorType: string; status: string; updatedAt: string; photos?: any[]; floorPlans?: any[] }>>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const scrollRestoredRef = React.useRef(false);
  const drawerRef = React.useRef<HTMLDivElement>(null);
  const handleTaskStatusChangeRef = React.useRef<((stageIndex: number, taskIndex: number, newStatus: string, workflowId?: string) => Promise<void>) | null>(null);

  // Fetch workflows for the property
  const fetchWorkflows = async () => {
    if (!propertyId || !agencySlug) return;
    try {
      const res = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/workflows`);
      setWorkflows(res.data);
      // Determine which workflow should be expanded
      const activeWorkflow = res.data.find((wf: Workflow) => wf.isActive === true);
      if (activeWorkflow) {
        setExpandedWorkflowId(activeWorkflow._id);
      } else if (property && (property.consultationStatus === 'in_progress' || property.consultationStatus === 'pending')) {
        setExpandedWorkflowId('initial-consultation');
      } else if (res.data.length > 0) {
        setExpandedWorkflowId(res.data[0]._id);
      } else if (property && property.stages && property.stages.length > 0) {
        setExpandedWorkflowId('initial-consultation');
      }
    } catch (err) {
      console.error('Error loading workflows:', err);
      if (property && property.stages && property.stages.length > 0) {
        setExpandedWorkflowId('initial-consultation');
      }
    }
  };

  // Fetch contractor jobs for summary
  const fetchContractorJobs = async () => {
    if (!propertyId || !agencySlug) return;
    try {
      setJobsLoading(true);
      const res = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`);
      setContractorJobs(res.data || []);
    } catch (e) {
      // ignore summary errors
    } finally {
      setJobsLoading(false);
    }
  };

  // Load workflows
  useEffect(() => {
    const loadWorkflows = async () => {
      console.log('🔄 LOADING WORKFLOWS - Function called');
      if (!property) {
        console.log('No property, skipping workflow load');
        return;
      }
      
      try {
        setWorkflowsLoading(true);
        setWorkflowsError(null);
        console.log('Fetching workflows for property:', property._id);
        
        // Add timeout to prevent infinite loading
        const timeoutPromise = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Workflow loading timeout')), 10000)
        );
        
        // Simple workflow fetch without complex detection logic
        const workflowsData = await Promise.race([
          getWorkflows(property._id, agencySlug),
          timeoutPromise
        ]) as Workflow[];
        
        console.log('Workflows fetched:', workflowsData.length);
        
        setWorkflows(workflowsData);
        
        // Check if there's a completed consultation workflow that should have created a next workflow
        if (property && workflowsData.length > 0) {
          const completedConsultation = workflowsData.find(
            (wf: Workflow) => wf.type === 'consultation' && wf.status === 'completed' && !wf.nextWorkflowId
          );
          // Don't show "missing workflow" if a following workflow (sale, rental, auction) already exists
          const hasFollowingWorkflow = workflowsData.some(
            (wf: Workflow) => wf.type === 'sale' || wf.type === 'rental' || wf.type === 'auction'
          );

          if (completedConsultation && !hasFollowingWorkflow) {
            const shouldCreate = await shouldCreateNextWorkflow(completedConsultation, property);
            if (shouldCreate) {
              // Extract sale type from the workflow
              const saleTypeTask = completedConsultation.stages
                ?.flatMap((s: any) => s.tasks)
                ?.find((t: any) => (t.taskType === 'sale_type_determination' || t.name === 'Determine Sale Type') && t.status === 'completed');
              const saleType = saleTypeTask?.fieldValues?.saleType || 'for sale';
              
              console.log('🔍 Missing workflow detected, attempting to create automatically...');
              // Try to create the workflow automatically
              try {
                const newWorkflow = await createNextWorkflow(completedConsultation, property);
                if (newWorkflow) {
                  console.log('✅ Workflow created automatically:', newWorkflow);
                  // Refresh workflows to show the new one
                  const refreshed = await getWorkflows(property._id, agencySlug);
                  setWorkflows(refreshed);
                  setMissingWorkflow(null);
                  
                  // Expand the newly created workflow
                  const createdWorkflow = refreshed.find((wf: Workflow) => wf._id === newWorkflow._id);
                  if (createdWorkflow) {
                    setExpandedWorkflowId(createdWorkflow._id);
                  }
                } else {
                  console.warn('⚠️ Automatic workflow creation failed, showing manual prompt');
                  setMissingWorkflow({ workflow: completedConsultation, saleType });
                }
              } catch (error) {
                console.error('❌ Error during automatic workflow creation:', error);
                setMissingWorkflow({ workflow: completedConsultation, saleType });
              }
            } else {
              setMissingWorkflow(null);
            }
          } else {
            setMissingWorkflow(null);
          }
        } else {
          setMissingWorkflow(null);
        }
        
        // Set expanded workflow
        const activeWorkflow = workflowsData.find((wf: Workflow) => wf.isActive === true);
        if (activeWorkflow) {
          setExpandedWorkflowId(activeWorkflow._id);
        } else if (workflowsData.length > 0) {
          setExpandedWorkflowId(workflowsData[0]._id);
        }
        
      } catch (error) {
        console.error('Error loading workflows:', error);
        setWorkflowsError('Failed to load workflows');
      } finally {
        setWorkflowsLoading(false);
      }
    };

    // Only load workflows if we don't already have workflows for this property
    if (workflows.length === 0) {
      loadWorkflows();
    } else {
      console.log('Workflows already loaded, skipping load to prevent state override');
    }
  }, [property?._id]);

  useEffect(() => {
    const fetchProperty = async () => {
      if (!propertyId || !agencySlug) {
        setError('Invalid property ID or agency slug');
        setLoading(false);
        return;
      }
      try {
        const data = await getProperty(agencySlug, propertyId);
        setProperty(data);
      } catch (err) {
        setError('Error loading property details');
        console.error('Error loading property:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [agencySlug, propertyId]);

  // Load contractor jobs summary when property or agency changes
  useEffect(() => {
    fetchContractorJobs();
  }, [agencySlug, propertyId]);

  // Listen for contractor job completion notifications and refresh files
  useEffect(() => {
    if (!user?.token || !agencySlug || !propertyId) return;

    const handleNotification = (notification: any) => {
      // Check if notification is about contractor task completion
      if (notification && notification.relatedEntity?.propertyId === propertyId) {
        const title = notification.title || '';
        const message = notification.message || '';
        
        // Check if notification is about contractor task completion
        // Title format: "Contractor task completed – [Type] – [Address]"
        // Message format: "The [type] task has been completed... Please check for new files and invoices."
        const isContractorTaskNotification = 
          title.includes('Contractor task completed') &&
          (message.includes('files') || message.includes('invoice') || message.includes('completed'));
        
        if (isContractorTaskNotification) {
          console.log('📁 Contractor task completion notification received, refreshing files...');
          // Trigger files refresh by updating the signal
          setRefreshFilesSignal(prev => prev + 1);
        }

        // Check if notification is about photographer job completion
        // Title format: "Job status updated – Photographer – [Address]"
        // Message format: "Job status changed from ... to completed..."
        const titleLower = title.toLowerCase();
        const messageLower = message.toLowerCase();
        const isPhotographerJobNotification = 
          titleLower.includes('job status updated') &&
          (titleLower.includes('photographer') || titleLower.includes('photography')) &&
          (messageLower.includes('to completed') || messageLower.includes('completed'));
        
        if (isPhotographerJobNotification) {
          console.log('📸 Photographer job completion notification received, refreshing property (photos & floor plans) and files...');
          // Refresh property data to get updated photos and floor plans
          const fetchProperty = async () => {
            try {
              const data = await getProperty(agencySlug, propertyId);
              setProperty(data);
              // Also trigger files refresh to update files section
              setRefreshFilesSignal(prev => prev + 1);
            } catch (err) {
              console.error('Error refreshing property after photographer job completion:', err);
            }
          };
          fetchProperty();
        }

        // Check if notification is about vendor responding to consultation report
        const isVendorResponseNotification =
          (title === 'Vendor responded to consultation report' || title.toLowerCase().includes('vendor responded')) &&
          (message.toLowerCase().includes('vendor') && message.toLowerCase().includes('response'));

        if (isVendorResponseNotification) {
          console.log('📩 Vendor response notification received, refreshing property and workflows...');
          const refetch = async () => {
            try {
              const data = await getProperty(agencySlug, propertyId);
              setProperty(data);
              const list = await getWorkflows(agencySlug, propertyId);
              setWorkflows(list);
            } catch (err) {
              console.error('Error refreshing after vendor response:', err);
            }
          };
          refetch();
        }

        // Check if notification is about report finalization
        // Title format: "Report finalized"
        // Message format: "Your market appraisal report has been finalized and is ready to send."
        const isReportFinalizedNotification = 
          (title === 'Report finalized' || title.toLowerCase().includes('report finalized')) &&
          (message.toLowerCase().includes('finalized') || message.toLowerCase().includes('ready to send'));

        if (isReportFinalizedNotification) {
          console.log('📄 Report finalized notification received, updating "Present Findings" task to in_progress...');
          
          // Find the consultation workflow and "Present Findings" task
          const consultationWorkflow = workflows.find((w: Workflow) => w.type === 'consultation');
          
          if (consultationWorkflow) {
            // Find the "Present Findings" task
            let foundStageIndex = -1;
            let foundTaskIndex = -1;
            
            for (let stageIdx = 0; stageIdx < consultationWorkflow.stages.length; stageIdx++) {
              const stage = consultationWorkflow.stages[stageIdx];
              for (let taskIdx = 0; taskIdx < stage.tasks.length; taskIdx++) {
                const task = stage.tasks[taskIdx];
                // Check by taskType first, then by name
                if (task.taskType === 'present_findings' || (task.name && task.name.toLowerCase() === 'present findings')) {
                  foundStageIndex = stageIdx;
                  foundTaskIndex = taskIdx;
                  break;
                }
              }
              if (foundStageIndex >= 0) break;
            }
            
            if (foundStageIndex >= 0 && foundTaskIndex >= 0) {
              const task = consultationWorkflow.stages[foundStageIndex].tasks[foundTaskIndex];
              // Only update if task is not already in_progress or completed
              if (task.status !== 'in_progress' && task.status !== 'completed') {
                console.log(`Updating "Present Findings" task (stage ${foundStageIndex}, task ${foundTaskIndex}) to in_progress`);
                const updateTask = handleTaskStatusChangeRef.current;
                if (updateTask) {
                  updateTask(foundStageIndex, foundTaskIndex, 'in_progress', consultationWorkflow._id).catch((err) => {
                    console.error('Error updating "Present Findings" task:', err);
                  });
                } else {
                  console.warn('handleTaskStatusChange not yet initialized');
                }
              } else {
                console.log(`"Present Findings" task is already ${task.status}, skipping update`);
              }
            } else {
              console.warn('"Present Findings" task not found in consultation workflow');
            }
          } else {
            console.warn('Consultation workflow not found');
          }
        }
      }
    };

    // Subscribe to notifications
    const unsubscribe = socketService.onNotification(handleNotification);

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.token, agencySlug, propertyId, workflows]);

  // Restore scroll position when returning from contractor jobs page
  useEffect(() => {
    if (!propertyId) return;
    
    const scrollKey = `property-scroll-${propertyId}`;
    const savedScroll = sessionStorage.getItem(scrollKey);
    
    // Reset the restored flag when propertyId changes
    if (scrollRestoredRef.current && !location.state?.restoreScroll) {
      scrollRestoredRef.current = false;
    }
    
    // Check if we should restore scroll (either from location.state or just have saved scroll)
    const shouldRestore = (location.state?.restoreScroll === true || 
                          (savedScroll && !loading && property)) &&
                          !scrollRestoredRef.current;
    
    if (shouldRestore && savedScroll) {
      scrollRestoredRef.current = true;
      const scrollY = parseInt(savedScroll, 10);
      
      console.log('[Scroll Restore] Attempting to restore scroll position to:', scrollY);
      console.log('[Scroll Restore] Current scroll position:', window.scrollY);
      
      // Disable browser's automatic scroll restoration temporarily
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
      
      // Multiple attempts to restore scroll in case of timing issues
      const restoreScroll = () => {
        const currentScroll = window.scrollY;
        if (Math.abs(currentScroll - scrollY) > 5) {
          console.log('[Scroll Restore] Restoring from', currentScroll, 'to', scrollY);
          window.scrollTo({
            top: scrollY,
            left: 0,
            behavior: 'auto'
          });
          return false; // Not yet restored
        }
        console.log('[Scroll Restore] Successfully restored to', window.scrollY);
        return true; // Successfully restored
      };
      
      // Immediate attempt
      restoreScroll();
      
      // Try multiple times with increasing delays
      const intervals = [50, 100, 200, 400, 800, 1500];
      let attemptCount = 0;
      
      const tryRestore = () => {
        if (attemptCount >= intervals.length) {
          console.log('[Scroll Restore] Final attempt completed, cleaning up');
          sessionStorage.removeItem(scrollKey);
          if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'auto';
          }
          return;
        }
        
        const restored = restoreScroll();
        attemptCount++;
        
        if (!restored && attemptCount < intervals.length) {
          const delay = attemptCount === 0 ? intervals[0] : intervals[attemptCount] - intervals[attemptCount - 1];
          setTimeout(tryRestore, delay);
        } else {
          console.log('[Scroll Restore] Scroll restored, cleaning up');
          sessionStorage.removeItem(scrollKey);
          if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'auto';
          }
        }
      };
      
      // Start the restoration attempts
      setTimeout(tryRestore, intervals[0]);
      
      // Also use requestAnimationFrame for smoother restoration
      requestAnimationFrame(() => {
        restoreScroll();
      });
    }
  }, [propertyId, loading, property, location.state]);

  // Click-outside detection to close drawer on desktop
  useEffect(() => {
    if (!detailsOpen || isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // If a Material UI Dialog/Modal is open and the click is inside it,
      // ignore the click so the drawer doesn't auto-close while using overlays
      // like the photo lightbox.
      const isInsideAnyModal = Boolean(
        target.closest('.MuiDialog-root') ||
        target.closest('.MuiDialog-container') ||
        target.closest('.MuiModal-root')
      );
      if (isInsideAnyModal) return;

      // Check if click is outside the drawer content
      // The drawerRef is attached to the content Box inside the drawer
      if (drawerRef.current && !drawerRef.current.contains(target as Node)) {
        // Also verify it's not within the drawer paper (in case of edge cases)
        const drawerPaper = target.closest('.MuiDrawer-paper');
        if (!drawerPaper) {
          setDetailsOpen(false);
        }
      }
    };

    // Add a small delay to prevent immediate closure when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [detailsOpen, isMobile]);

  // Expose fetchProperty for child components
  const refetchProperty = async () => {
    if (!propertyId || !agencySlug) return;
    try {
      const data = await getProperty(agencySlug, propertyId);
      setProperty(data);
    } catch (err) {
      setError('Error loading property details');
    }
  };

  const handleTaskStatusChange = async (stageIndex: number, taskIndex: number, newStatus: string, workflowId?: string, fieldValues?: any) => {
    console.log('=== PROPERTY DETAILS TASK STATUS CHANGE ===');
    console.log('Stage Index:', stageIndex);
    console.log('Task Index:', taskIndex);
    console.log('New Status:', newStatus);
    console.log('Workflow ID:', workflowId);
    console.log('Field Values:', fieldValues);
    console.log('Property exists:', !!property);
    console.log('Property ID:', propertyId);
    console.log('Agency Slug:', agencySlug);
    
    if (!property || !propertyId || !agencySlug) {
      console.warn('Missing required data for task update');
      return;
    }

    // Only allow task updates for workflow-based tasks (with workflowId)
    if (!workflowId) {
      console.warn('Task status updates are only supported for workflow-based tasks');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${taskIndex}`;
      
      console.log('=== TASK STATUS UPDATE ===');
      console.log('URL:', url);
      console.log('Method: PATCH');
      console.log('Stage Index:', stageIndex);
      console.log('Task Index:', taskIndex);
      console.log('New Status:', newStatus);
      console.log('Workflow ID:', workflowId);
      console.log('Token exists:', !!token);
        
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          status: newStatus,
          fieldValues: fieldValues // Pass fieldValues through to backend
        })
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        let errorMessage = `Failed to update task status: ${response.status} ${response.statusText}`;
        try {
          const errorText = await response.text();
          console.error('Response error text:', errorText);
          // Try to parse as JSON to get the error message
          const errorData = JSON.parse(errorText);
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseErr) {
          // If parsing fails, use the default message
          console.error('Could not parse error response:', parseErr);
        }
        throw new Error(errorMessage);
      }

      // Get the updated workflow from the response
      const updatedWorkflow = await response.json();
      console.log('=== FRONTEND WORKFLOW UPDATE ===');
      console.log('Updated workflow from response:', updatedWorkflow);
      console.log('Updated workflow status:', updatedWorkflow.status);
      console.log('Updated workflow ID:', updatedWorkflow._id);

      // Update the workflows state with the updated workflow
      console.log('Previous workflows:', workflows);
      setWorkflows(prevWorkflows => {
        const updatedWorkflows = prevWorkflows.map(w => {
          if (w._id === workflowId) {
            console.log('Updating workflow:', w._id, 'from status:', w.status, 'to:', updatedWorkflow.status);
            console.log('Updated workflow stages:', updatedWorkflow.stages.map((s: any) => ({ name: s.name, status: s.status })));
            return updatedWorkflow;
          }
          return w;
        });
        console.log('Updated workflows state:', updatedWorkflows);
        return updatedWorkflows;
      });

      // If the workflow was just completed, refresh the entire workflows list
      // to get any newly created workflows (like auction workflows)
      if (updatedWorkflow.status === 'completed') {
        console.log('Workflow completed, refreshing workflows list to get new workflows');
        console.log('Completed workflow type:', updatedWorkflow.type);
        console.log('Completed workflow name:', updatedWorkflow.name);
        
        try {
          const res = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/workflows`);
          console.log('Refreshed workflows list:', res.data);
          setWorkflows(res.data);
          
          // Set the newly created workflow as expanded if it exists
          const newWorkflow = res.data.find((wf: Workflow) => 
            wf.previousWorkflowId === workflowId && wf.isActive === true
          );
          if (newWorkflow) {
            console.log('Found new workflow:', newWorkflow.name, 'setting as expanded');
            setExpandedWorkflowId(newWorkflow._id);
          } else {
            console.log('No new workflow found with previousWorkflowId:', workflowId);
            console.log('Available workflows:', res.data.map((wf: Workflow) => ({ 
              id: wf._id, 
              name: wf.name, 
              previousWorkflowId: wf.previousWorkflowId,
              isActive: wf.isActive 
            })));
            
            // If no new workflow found immediately, try again after a short delay
            // in case the workflow creation is delayed
            setTimeout(async () => {
              try {
                console.log('Retrying workflow refresh after delay...');
                const retryRes = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/workflows`);
                setWorkflows(retryRes.data);
                
                const retryNewWorkflow = retryRes.data.find((wf: Workflow) => 
                  wf.previousWorkflowId === workflowId && wf.isActive === true
                );
                if (retryNewWorkflow) {
                  console.log('Found new workflow on retry:', retryNewWorkflow.name, 'setting as expanded');
                  setExpandedWorkflowId(retryNewWorkflow._id);
                }
              } catch (retryErr) {
                console.error('Error on retry workflow refresh:', retryErr);
              }
            }, 2000); // Wait 2 seconds before retrying
          }
        } catch (err) {
          console.error('Error refreshing workflows after completion:', err);
        }
      }

      // Trigger property refresh to get updated styling company information
      // when tasks that affect property data are completed
      refetchProperty();
      
      // Clear optimistic updates by triggering handleTaskPatched
      handleTaskPatched();
    } catch (err: any) {
      console.error('Error updating task status:', err);
      setError(err?.message || 'Error updating task status');
    }
  };

  // Store handleTaskStatusChange in ref for use in notification handler
  // Update ref whenever the function is recreated (it's stable but we update ref for safety)
  handleTaskStatusChangeRef.current = handleTaskStatusChange;

  const handleTaskPatched = (options?: { skipFiles?: boolean; skipWorkflows?: boolean; skipProperty?: boolean }) => {
    if (!options?.skipProperty) {
      // Refetch property data to get updated styling company information
      // when styling arrangement task is completed
      refetchProperty();
    }

    if (!options?.skipWorkflows) {
      // Refresh workflows first, THEN trigger Files refresh - so WorkflowFiles gets updated rpDataFiles
      (async () => {
        try {
          if (propertyId && agencySlug && property) {
            const refreshed = await getWorkflows(propertyId, agencySlug);
            setWorkflows(refreshed);
            
            // Trigger Files section refresh after workflows are updated (for RP data etc.)
            if (!options?.skipFiles) {
              setRefreshFilesSignal(s => s + 1);
            }
            
            // Re-check for missing workflows after refresh
            if (refreshed.length > 0) {
              const completedConsultation = refreshed.find(
                (wf: Workflow) => wf.type === 'consultation' && wf.status === 'completed' && !wf.nextWorkflowId
              );
              const hasFollowingWorkflow = refreshed.some(
                (wf: Workflow) => wf.type === 'sale' || wf.type === 'rental' || wf.type === 'auction'
              );

              if (completedConsultation && !hasFollowingWorkflow) {
                const shouldCreate = await shouldCreateNextWorkflow(completedConsultation, property);
                if (shouldCreate) {
                  const saleTypeTask = completedConsultation.stages
                    ?.flatMap((s: any) => s.tasks)
                    ?.find((t: any) => (t.taskType === 'sale_type_determination' || t.name === 'Determine Sale Type') && t.status === 'completed');
                  const saleType = saleTypeTask?.fieldValues?.saleType || 'for sale';
                  setMissingWorkflow({ workflow: completedConsultation, saleType });
                } else {
                  setMissingWorkflow(null);
                }
              } else {
                setMissingWorkflow(null);
              }
            } else {
              setMissingWorkflow(null);
            }
          }
        } catch (e) {
          console.error('Error refreshing workflows after task patch:', e);
        }
      })();
    } else if (!options?.skipFiles) {
      // When skipping workflow refresh, still trigger Files refresh
      setRefreshFilesSignal(s => s + 1);
    }
  };

  // Check if current user can edit this property (is any assignee or admin)
  const canEdit = Boolean(user?.primaryRole === 'admin' || 
    (property?.primaryAssignee && property.primaryAssignee === user?.id) || 
    (property?.secondaryAssignee && property.secondaryAssignee === user?.id) || 
    (property?.tertiaryAssignee && property.tertiaryAssignee === user?.id));

  // Handle creating missing workflow
  const handleCreateMissingWorkflow = async () => {
    if (!missingWorkflow || !property || !agencySlug) return;
    
    try {
      setCreatingWorkflow(true);
      const newWorkflow = await createNextWorkflow(missingWorkflow.workflow, property);
      
      if (newWorkflow) {
        // Refresh workflows
        const refreshed = await getWorkflows(property._id, agencySlug);
        setWorkflows(refreshed);
        setMissingWorkflow(null);
        
        // Expand the newly created workflow
        const createdWorkflow = refreshed.find((wf: Workflow) => wf._id === newWorkflow._id);
        if (createdWorkflow) {
          setExpandedWorkflowId(createdWorkflow._id);
        }
      } else {
        setWorkflowsError('Failed to create workflow. Please try again.');
      }
    } catch (error) {
      console.error('Error creating missing workflow:', error);
      setWorkflowsError('Failed to create workflow. Please try again.');
    } finally {
      setCreatingWorkflow(false);
    }
  };

  const handleStartCampaign = async (workflowType: string, saleType: string) => {
    if (!property || !agencySlug) return;
    try {
        setCreatingWorkflow(true);
        const newWorkflow = await createSpecificWorkflow(agencySlug, property._id, workflowType, saleType);
        if (newWorkflow) {
             // Refresh workflows
             const refreshed = await getWorkflows(property._id, agencySlug);
             setWorkflows(refreshed);
             // Refresh property details to update saleType
             await refetchProperty();
             const createdWorkflow = refreshed.find((wf: Workflow) => wf._id === newWorkflow._id);
             if (createdWorkflow) {
               setExpandedWorkflowId(createdWorkflow._id);
             }
        } else {
             setWorkflowsError('Failed to create workflow');
        }
    } catch (e) {
        console.error(e);
        setWorkflowsError('Failed to create workflow');
    } finally {
        setCreatingWorkflow(false);
    }
  };

  const propertyViewMode: PropertyViewMode = getPropertyViewMode(user);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" gutterBottom>{error}</Typography>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/agencies/${agencySlug}/properties`)}
        >
          Back to Properties
        </Button>
      </Box>
    );
  }

  if (!property) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" gutterBottom>Property not found</Typography>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/agencies/${agencySlug}/properties`)}
        >
          Back to Properties
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* Warning banner for inactive subscription */}
      {!isSubscriptionActive && showInactiveSubscriptionNotice && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <Alert
            severity="warning"
            onClose={() => setShowInactiveSubscriptionNotice(false)}
            sx={{ fontWeight: 600 }}
          >
            Your paid subscription isn’t active yet. Property tools remain available, but Free plan limits apply until payment succeeds.
          </Alert>
        </Box>
      )}
      {/* Main content - Workflow */}
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/agencies/${agencySlug}/properties`)}
          >
            Back to Properties
          </Button>
        </Box>

        {/* Property Address Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <LocationOn color="primary" />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            {(() => {
              // Prefer formattedAddress first line (matches app and includes unit/apt e.g. "12/3 Charles St")
              if (property.address?.formattedAddress) {
                const formattedParts = property.address.formattedAddress.split(',');
                const formattedStreet = formattedParts[0]?.trim() || '';
                if (formattedStreet) return formattedStreet;
              }
              if (property.address?.street) return property.address.street;
              return 'Property Address';
            })()}
          </Typography>
          {property.status === 'sold' && (
            <Chip
              icon={<SoldIcon />}
              label="SOLD"
              color="success"
              size="small"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                height: 28,
                ml: 0.5,
              }}
            />
          )}
        </Box>
        {property.address?.city && property.address?.state && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, ml: 4 }}>
            {[property.address.city, property.address.state, property.address.postalCode].filter(Boolean).join(', ')}
            {property.address.country && property.address.country !== 'Australia' ? `, ${property.address.country}` : ''}
          </Typography>
        )}

        {/* Open Schedule Section */}
        <OpenScheduleCard 
          propertyId={propertyId as string} 
          agencySlug={agencySlug as string} 
          workflows={workflows}
          property={property}
        />

        {/* Contractor Jobs Section (expandable like Files) */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WorkIcon fontSize="small" />
              Contractor Jobs
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Overview
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  // Store current scroll position before navigating
                  const scrollKey = `property-scroll-${propertyId}`;
                  sessionStorage.setItem(scrollKey, window.scrollY.toString());
                  navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`);
                }}
              >
                Create New Job
              </Button>
            </Box>
            {!jobsLoading && contractorJobs.length > 0 ? (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Contractor</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Photos</TableCell>
                        <TableCell>Floor Plans</TableCell>
                        <TableCell>Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contractorJobs.map((job) => (
                        <TableRow key={job._id} hover>
                          <TableCell>{job.contractorType?.replace('_', ' ')}</TableCell>
                          <TableCell>{job.contractorName}</TableCell>
                          <TableCell>
                            <Chip size="small" label={job.status.replace('_', ' ')} color={job.status === 'completed' ? 'success' : job.status === 'in_progress' ? 'primary' : job.status === 'pending' ? 'warning' : 'default'} />
                          </TableCell>
                          <TableCell>{job.photos?.length || 0}</TableCell>
                          <TableCell>{job.floorPlans?.length || 0}</TableCell>
                          <TableCell>{new Date(job.updatedAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box mt={1} textAlign="right">
                  <Button size="small" onClick={() => {
                    // Store current scroll position before navigating
                    const scrollKey = `property-scroll-${propertyId}`;
                    sessionStorage.setItem(scrollKey, window.scrollY.toString());
                    navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`);
                  }}>Open Contractor Jobs</Button>
                </Box>
              </>
            ) : jobsLoading ? (
              <Box display="flex" alignItems="center" gap={1}><CircularProgress size={20} /><Typography variant="body2">Loading jobs…</Typography></Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No contractor jobs assigned to this property yet.</Typography>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Rental applications (rental properties only) */}
        {property.saleType === 'rental' && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ApplicationIcon fontSize="small" />
                Rental applications
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {!isRentalApplicationSupported(property.address?.state) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Online application form is not available for properties in this state. Please contact the agent for a paper form or alternative.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Share the form link with interested tenants so they can apply; they get their own link and code to view status after submitting. Manage applications and approvals.
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  const scrollKey = `property-scroll-${propertyId}`;
                  sessionStorage.setItem(scrollKey, window.scrollY.toString());
                  navigate(`/agencies/${agencySlug}/properties/${propertyId}/rental-applications`);
                }}
              >
                Open rental applications
              </Button>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Files Section (aggregated) */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderIcon fontSize="small" />
              Files
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <WorkflowFiles
              agencySlug={agencySlug as string}
              propertyId={propertyId as string}
              workflows={workflows}
              property={property}
              refreshKey={refreshFilesSignal}
              onChange={handleTaskPatched}
            />
          </AccordionDetails>
        </Accordion>

        {/* Attendees Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={2}>
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon fontSize="small" />
                Attendees
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/agencies/${agencySlug}/properties/${propertyId}/visitor-inspection-report`);
                }}
              >
                Visitor Inspection Report
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <PropertyAttendees
              property={property}
              agencySlug={agencySlug as string}
            />
          </AccordionDetails>
        </Accordion>

        {/* Workflows Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountTreeIcon fontSize="small" />
              Workflows
            </Typography>
            
            {workflowsLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography>Loading workflows...</Typography>
              </Box>
            )}
            
            {workflowsError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setWorkflowsError(null)}>
                {workflowsError}
              </Alert>
            )}
            
            {/* Alert for missing workflow */}
            {!workflowsLoading && missingWorkflow && (
              <Alert 
                severity="warning" 
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleCreateMissingWorkflow}
                    disabled={creatingWorkflow}
                  >
                    {creatingWorkflow ? 'Creating...' : `Create ${missingWorkflow.saleType === 'auction' || missingWorkflow.saleType === 'pre-auction' ? 'Auction' : 'Sale'} Workflow`}
                  </Button>
                }
              >
                The initial consultation workflow has been completed, but no following workflow was created. 
                {missingWorkflow.saleType ? (
                  <> Click the button to create the {missingWorkflow.saleType === 'auction' || missingWorkflow.saleType === 'pre-auction' ? 'auction' : 'sale'} workflow.</>
                ) : (
                  <> Please ensure the "Determine Sale Type" task is completed in the consultation workflow.</>
                )}
              </Alert>
            )}
            
            {!workflowsLoading && workflows.length === 0 && property.consultationStatus === 'completed' && (
               <Box sx={{ p: 3, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 2, mt: 2 }}>
                 <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                   {propertyViewMode === 'rental'
                     ? 'Start Rental Campaign'
                     : propertyViewMode === 'sales'
                     ? 'Start Sales Campaign'
                     : 'Start Campaign'}
                 </Typography>
                 <Typography variant="body2" color="text.secondary" paragraph>
                   Initial consultation was skipped or completed. Choose a workflow to proceed.
                 </Typography>
                 <Grid container spacing={2} justifyContent="center" sx={{ mt: 1 }}>
                   {(propertyViewMode === 'sales' || propertyViewMode === 'both') && (
                     <>
                       <Grid item>
                         <Button 
                           variant="contained" 
                           color="primary"
                           disabled={creatingWorkflow}
                           onClick={() => handleStartCampaign('sale', 'for sale')}
                           sx={{ minWidth: 160 }}
                         >
                           Private Treaty
                         </Button>
                       </Grid>
                       <Grid item>
                         <Button 
                           variant="contained" 
                           color="info"
                           disabled={creatingWorkflow}
                           onClick={() => handleStartCampaign('sale', 'off-market')}
                           sx={{ minWidth: 160 }}
                         >
                           Off Market
                         </Button>
                       </Grid>
                       <Grid item>
                         <Button 
                           variant="contained" 
                           color="warning"
                           disabled={creatingWorkflow}
                           onClick={() => handleStartCampaign('auction', 'pre-auction')}
                           sx={{ minWidth: 160 }}
                         >
                           Pre-Auction
                         </Button>
                       </Grid>
                       <Grid item>
                         <Button 
                           variant="contained" 
                           color="secondary"
                           disabled={creatingWorkflow}
                           onClick={() => handleStartCampaign('auction', 'auction')}
                           sx={{ minWidth: 160 }}
                         >
                           Auction
                         </Button>
                       </Grid>
                     </>
                   )}
                   {(propertyViewMode === 'rental' || propertyViewMode === 'both') && (
                     <Grid item>
                       <Button 
                         variant="contained" 
                         color="success"
                         disabled={creatingWorkflow}
                         onClick={() => handleStartCampaign('rental', 'rental')}
                         sx={{ minWidth: 160 }}
                       >
                         Rental
                       </Button>
                     </Grid>
                   )}
                 </Grid>
               </Box>
            )}
            
            {!workflowsLoading && workflows.length === 0 && property.consultationStatus !== 'completed' && (
              <Typography color="text.secondary">
                No workflows found for this property.
              </Typography>
            )}
            
            {/* Legacy workflow support for properties with stages but no workflows */}
            {!workflowsLoading && workflows.length === 0 && property.stages && property.stages.length > 0 && (
              <Accordion
                key="legacy-workflow"
                expanded={expandedWorkflowId === 'legacy-workflow'}
                onChange={() => setExpandedWorkflowId(expandedWorkflowId === 'legacy-workflow' ? null : 'legacy-workflow')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography>Initial Consultation (Legacy)</Typography>
                    <Chip label="Legacy" color="warning" size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <PropertyWorkflow
                    stages={property.stages}
                    currentStage={property.currentStage}
                    onTaskStatusChange={undefined}
                    agencySlug={agencySlug as string}
                    propertyId={propertyId as string}
                    onTaskPatched={handleTaskPatched}
                    priceRange={property.priceRange}
                    editable={false}
                    propertyStylingCompanyName={property.stylingCompany?.name}
                    propertyStatus={property.status}
                    propertyListingUrls={property.listingUrls}
                    consultationReportExists={Boolean(property.consultationReport && property.consultationReport.url && ((property.consultationReport as any).status === 'ready' || (property.consultationReport as any).status === 'draft' || (property.consultationReport as any).status === 'finalized'))}
                    consultationReportUrl={(property.consultationReport && property.consultationReport.url) ? property.consultationReport.url : undefined}
                    consultationReportSentAt={(property.consultationReport as any)?.sentAt || null}
                    consultationReportPresentFindingsCompleted={(() => {
                      // Check if "Present Findings" task is completed in any workflow
                      for (const wf of workflows) {
                        if (wf.type === 'consultation') {
                          for (const stage of wf.stages || []) {
                            for (const task of stage.tasks || []) {
                              if (task.taskType === 'present_findings' && task.status === 'completed') {
                                return true;
                              }
                            }
                          }
                        }
                      }
                      return false;
                    })()}
                    refreshKey={refreshFilesSignal}
                    ownerEmail={getPrimaryOwnerEmail(property.owner)}
                  />
                </AccordionDetails>
              </Accordion>
            )}
            
            {!workflowsLoading && workflows.length > 0 && workflows.map((workflow) => (
              <Accordion
                key={workflow._id}
                expanded={expandedWorkflowId === workflow._id}
                onChange={() => setExpandedWorkflowId(expandedWorkflowId === workflow._id ? null : workflow._id)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography>
                      {(workflow.type === 'consultation' || workflow.name.startsWith('Initial Consultation'))
                        ? `Initial Consultation - ${property.address?.formattedAddress || 
                            `${property.address?.street}, ${property.address?.city} ${property.address?.state} ${property.address?.postalCode}`}`
                        : workflow.name}
                    </Typography>
                    <Chip 
                      label={workflow.status} 
                      color={workflow.status === 'completed' ? 'success' : workflow.status === 'in_progress' ? 'primary' : 'default'}
                      size="small"
                    />
                    {workflow.isActive && (
                      <Chip label="Active" color="secondary" size="small" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <PropertyWorkflow
                    stages={workflow.stages}
                    currentStage={workflow.currentStage}
                    onTaskStatusChange={canEdit ? handleTaskStatusChange : undefined}
                    agencySlug={agencySlug as string}
                    propertyId={propertyId as string}
                    workflowId={workflow._id}
                    onTaskPatched={handleTaskPatched}
                    priceRange={property.priceRange}
                    editable={canEdit}
                    propertyStylingCompanyName={property.stylingCompany?.name}
                    propertyStatus={property.status}
                    propertyListingUrls={property.listingUrls}
                    property={property}
                    propertySaleType={(() => {
                      // Use property.saleType if available (set during initial consultation)
                      if (property?.saleType) {
                        if (property.saleType === 'auction') return 'auction';
                        if (property.saleType === 'rental') return 'rental';
                        return 'sale';
                      }
                      
                      // Fallback to deriving from workflows if saleType not set yet
                      // This should only happen for older properties without saleType set
                      if (!workflows || workflows.length === 0) return undefined;
                      
                      const activeWorkflow = workflows.find((w: any) => w.isActive === true);
                      
                      if (activeWorkflow) {
                        if (activeWorkflow.type === 'auction') {
                          return 'auction';
                        }
                        if (activeWorkflow.type === 'rental') {
                          return 'rental';
                        }
                        if (activeWorkflow.saleType) {
                          if (activeWorkflow.saleType === 'auction' || activeWorkflow.saleType === 'pre-auction') {
                            return 'auction';
                          }
                        }
                      }
                      
                      return 'sale';
                    })()}
                    consultationReportExists={Boolean(property.consultationReport && property.consultationReport.url && ((property.consultationReport as any).status === 'ready' || (property.consultationReport as any).status === 'draft' || (property.consultationReport as any).status === 'finalized'))}
                    consultationReportUrl={(property.consultationReport && property.consultationReport.url) ? property.consultationReport.url : undefined}
                    consultationReportSentAt={(property.consultationReport as any)?.sentAt || null}
                    consultationReportPresentFindingsCompleted={(() => {
                      // Check if "Present Findings" task is completed in the current workflow or any consultation workflow
                      if (workflow.type === 'consultation') {
                        for (const stage of workflow.stages || []) {
                          for (const task of stage.tasks || []) {
                            if (task.taskType === 'present_findings' && task.status === 'completed') {
                              return true;
                            }
                          }
                        }
                      }
                      // Also check other consultation workflows
                      for (const wf of workflows) {
                        if (wf.type === 'consultation' && wf._id !== workflow._id) {
                          for (const stage of wf.stages || []) {
                            for (const task of stage.tasks || []) {
                              if (task.taskType === 'present_findings' && task.status === 'completed') {
                                return true;
                              }
                            }
                          }
                        }
                      }
                      return false;
                    })()}
                    refreshKey={refreshFilesSignal}
                    ownerEmail={getPrimaryOwnerEmail(property.owner)}
                    consultationVendorResponse={(property.consultationReport as any)?.vendorResponse}
                    consultationVendorResponseDate={(property.consultationReport as any)?.vendorResponseDate}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      </Box>

      {/* Property Details Drawer */}
      {(isMobile || detailsOpen) && (
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="right"
        open={detailsOpen}
        onClose={() => isMobile && setDetailsOpen(false)}
        sx={{
          width: isMobile ? '100%' : 400,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isMobile ? '100%' : 400,
            boxSizing: 'border-box',
            position: 'relative',
          },
        }}
      >
        <Box ref={drawerRef} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Property Details</Typography>
            <Box>
              {isMobile && (
                <IconButton onClick={() => setDetailsOpen(false)} sx={{ mr: 1 }}>
                  <CloseIcon />
                </IconButton>
              )}
              <Tooltip title={detailsOpen ? "Hide details" : "Show details"}>
                <IconButton
                  onClick={() => setDetailsOpen(!detailsOpen)}
                  sx={{
                    transform: detailsOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                    transition: 'transform 0.3s',
                  }}
                >
                  {detailsOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <PropertyDetailsContent 
            property={property} 
            agencySlug={agencySlug as string} 
            refreshFilesSignal={refreshFilesSignal}
            onPropertyUpdate={setProperty}
          />
          {canEdit && (
            <Box sx={{ mt: 2 }}>
              {property.status !== 'archived' && property.status !== 'sold' && property.saleType !== 'rental' && (
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  startIcon={<SoldIcon />}
                  onClick={() => {
                    if (!agencySlug || !propertyId) return;
                    setMarkSoldFormData({
                      soldPrice: property.soldPrice?.toString() || '',
                      purchasers: property.purchasers && property.purchasers.length > 0 
                        ? property.purchasers.map(p => ({
                            firstName: p.firstName || '',
                            lastName: p.lastName || '',
                            email: p.email || '',
                            phone: p.phone || '',
                            address: p.address || '',
                            city: p.city || '',
                            state: p.state || '',
                            postcode: p.postcode || '',
                            notes: p.notes || '',
                          }))
                        : [{
                            firstName: '',
                            lastName: '',
                            email: '',
                            phone: '',
                            address: '',
                            city: '',
                            state: '',
                            postcode: '',
                            notes: '',
                          }],
                      createCustomer: true,
                    });
                    setMarkSoldDialogOpen(true);
                  }}
                  sx={{ mb: 2 }}
                >
                  Mark as Sold
                </Button>
              )}
              {property.saleType === 'rental' && property.status !== 'archived' && property.status !== 'leased' && (
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  startIcon={<SoldIcon />}
                  sx={{ mb: 2 }}
                  onClick={async () => {
                    if (!agencySlug || !propertyId) return;
                    try {
                      await updateProperty(agencySlug, propertyId, { status: 'leased' });
                      await refetchProperty();
                    } catch (err: any) {
                      console.error('Mark as leased failed:', err);
                    }
                  }}
                >
                  Mark as Leased
                </Button>
              )}
              {property.status !== 'archived' ? (
                <Button
                  variant="outlined"
                  color="warning"
                  fullWidth
                  onClick={() => {
                    if (!agencySlug || !propertyId) return;
                    setArchiveDialogOpen(true);
                  }}
                >
                  Archive Property
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  onClick={async () => {
                    if (!agencySlug || !propertyId) return;
                    await updateProperty(agencySlug, propertyId, { status: 'consultation' });
                    await refetchProperty();
                  }}
                >
                  Restore Property
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Drawer>
      )}

      {/* Mark as Sold Dialog */}
      <Dialog open={markSoldDialogOpen} onClose={() => !markingAsSold && setMarkSoldDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Mark Property as Sold</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please provide purchaser contact information and optional sale price to mark this property as sold.
          </Typography>
          
          <NumericFormat
            customInput={TextField}
            label="Sold Price ($)"
            value={markSoldFormData.soldPrice || ''}
            onValueChange={(values) => {
              setMarkSoldFormData({ ...markSoldFormData, soldPrice: values.value || '' });
            }}
            thousandSeparator={true}
            prefix="$"
            decimalScale={0}
            allowNegative={false}
            fullWidth
            margin="normal"
            helperText="Optional - can be recorded later"
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>
            Purchaser Information
            {markSoldFormData.purchasers.length > 1 && ` (${markSoldFormData.purchasers.length} purchasers)`}
          </Typography>
          
          {markSoldFormData.purchasers.map((purchaser, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {index === 0 ? 'Primary Purchaser' : `Purchaser ${index + 1}`}
                </Typography>
                {markSoldFormData.purchasers.length > 1 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      const newPurchasers = markSoldFormData.purchasers.filter((_, i) => i !== index);
                      setMarkSoldFormData({ ...markSoldFormData, purchasers: newPurchasers });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="First Name"
                    value={purchaser.firstName}
                    onChange={(e) => {
                      const newPurchasers = [...markSoldFormData.purchasers];
                      newPurchasers[index] = { ...newPurchasers[index], firstName: e.target.value };
                      setMarkSoldFormData({ ...markSoldFormData, purchasers: newPurchasers });
                    }}
                    fullWidth
                    margin="normal"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Last Name"
                    value={purchaser.lastName}
                    onChange={(e) => {
                      const newPurchasers = [...markSoldFormData.purchasers];
                      newPurchasers[index] = { ...newPurchasers[index], lastName: e.target.value };
                      setMarkSoldFormData({ ...markSoldFormData, purchasers: newPurchasers });
                    }}
                    fullWidth
                    margin="normal"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    type="email"
                    value={purchaser.email}
                    onChange={(e) => {
                      const newPurchasers = [...markSoldFormData.purchasers];
                      newPurchasers[index] = { ...newPurchasers[index], email: e.target.value };
                      setMarkSoldFormData({ ...markSoldFormData, purchasers: newPurchasers });
                    }}
                    fullWidth
                    margin="normal"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone"
                    value={purchaser.phone}
                    onChange={(e) => {
                      const newPurchasers = [...markSoldFormData.purchasers];
                      newPurchasers[index] = { ...newPurchasers[index], phone: e.target.value };
                      setMarkSoldFormData({ ...markSoldFormData, purchasers: newPurchasers });
                    }}
                    fullWidth
                    margin="normal"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address"
                    value={purchaser.address}
                    onChange={(e) => {
                      const newPurchasers = [...markSoldFormData.purchasers];
                      newPurchasers[index] = { ...newPurchasers[index], address: e.target.value };
                      setMarkSoldFormData({ ...markSoldFormData, purchasers: newPurchasers });
                    }}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="City"
                    value={purchaser.city}
                    onChange={(e) => {
                      const newPurchasers = [...markSoldFormData.purchasers];
                      newPurchasers[index] = { ...newPurchasers[index], city: e.target.value };
                      setMarkSoldFormData({ ...markSoldFormData, purchasers: newPurchasers });
                    }}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="State"
                    value={purchaser.state}
                    onChange={(e) => {
                      const newPurchasers = [...markSoldFormData.purchasers];
                      newPurchasers[index] = { ...newPurchasers[index], state: e.target.value };
                      setMarkSoldFormData({ ...markSoldFormData, purchasers: newPurchasers });
                    }}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Postcode"
                    value={purchaser.postcode}
                    onChange={(e) => {
                      const newPurchasers = [...markSoldFormData.purchasers];
                      newPurchasers[index] = { ...newPurchasers[index], postcode: e.target.value };
                      setMarkSoldFormData({ ...markSoldFormData, purchasers: newPurchasers });
                    }}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    value={purchaser.notes}
                    onChange={(e) => {
                      const newPurchasers = [...markSoldFormData.purchasers];
                      newPurchasers[index] = { ...newPurchasers[index], notes: e.target.value };
                      setMarkSoldFormData({ ...markSoldFormData, purchasers: newPurchasers });
                    }}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
              
              {index < markSoldFormData.purchasers.length - 1 && <Divider sx={{ mt: 2, mb: 2 }} />}
            </Box>
          ))}
          
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setMarkSoldFormData({
                  ...markSoldFormData,
                  purchasers: [...markSoldFormData.purchasers, {
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    address: '',
                    city: '',
                    state: '',
                    postcode: '',
                    notes: '',
                  }],
                });
              }}
              size="small"
            >
              Add Another Purchaser
            </Button>
          </Box>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={markSoldFormData.createCustomer}
                  onChange={(e) => setMarkSoldFormData({ ...markSoldFormData, createCustomer: e.target.checked })}
                />
              }
              label="Create customer records for all purchasers"
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkSoldDialogOpen(false)} disabled={markingAsSold}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!agencySlug || !propertyId) return;
              
              // Validate all purchasers
              for (let i = 0; i < markSoldFormData.purchasers.length; i++) {
                const purchaser = markSoldFormData.purchasers[i];
                if (!purchaser.firstName || !purchaser.lastName || !purchaser.email || !purchaser.phone) {
                  alert(`Please fill in all required fields for Purchaser ${i + 1} (First Name, Last Name, Email, Phone)`);
                  return;
                }
              }
              
              try {
                setMarkingAsSold(true);
                const soldPriceValue = markSoldFormData.soldPrice && markSoldFormData.soldPrice.trim() !== '' 
                  ? parseFloat(markSoldFormData.soldPrice.replace(/[^0-9.]/g, '')) 
                  : undefined;
                
                await markPropertyAsSold(agencySlug, propertyId, {
                  soldPrice: soldPriceValue,
                  purchasers: markSoldFormData.purchasers.map(p => ({
                    firstName: p.firstName.trim(),
                    lastName: p.lastName.trim(),
                    email: p.email.trim(),
                    phone: p.phone.trim(),
                    address: p.address.trim() || undefined,
                    city: p.city.trim() || undefined,
                    state: p.state.trim() || undefined,
                    postcode: p.postcode.trim() || undefined,
                    notes: p.notes.trim() || undefined,
                  })),
                  createCustomer: markSoldFormData.createCustomer,
                });
                await refetchProperty();
                setMarkSoldDialogOpen(false);
                // Reset form
                setMarkSoldFormData({
                  soldPrice: '',
                  purchasers: [{
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    address: '',
                    city: '',
                    state: '',
                    postcode: '',
                    notes: '',
                  }],
                  createCustomer: true,
                });
              } catch (error: any) {
                console.error('Failed to mark property as sold:', error);
                alert(error.response?.data?.error || error.response?.data?.message || 'Failed to mark property as sold. Please try again.');
              } finally {
                setMarkingAsSold(false);
              }
            }}
            variant="contained"
            color="success"
            disabled={markingAsSold}
            startIcon={<SoldIcon />}
          >
            {markingAsSold ? 'Marking as Sold...' : 'Mark as Sold'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Property Confirmation Dialog */}
      <Dialog open={archiveDialogOpen} onClose={() => setArchiveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Archive Property</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Archiving this property will cancel all its upcoming calendar events and open sessions. Do you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialogOpen(false)} variant="outlined">Cancel</Button>
          <Button
            onClick={async () => {
              if (!agencySlug || !propertyId) return;
              try {
                await updateProperty(agencySlug, propertyId, { status: 'archived' });
                await refetchProperty();
              } finally {
                setArchiveDialogOpen(false);
              }
            }}
            variant="contained"
            color="warning"
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating action button to show details when hidden */}
      {!detailsOpen && !isMobile && (
        <Tooltip title="Show property details">
          <Fab
            color="primary"
            size="small"
            onClick={() => setDetailsOpen(true)}
            sx={{
              position: 'fixed',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              '&:hover': {
                transform: 'translateY(-50%) scale(1.1)',
              },
            }}
          >
            <InfoIcon />
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
};

export default PropertyDetails; 